package org.vreo.devicehub.features.core.data.protocolBuffer.mappers

import org.vreo.devicehub.features.core.domain.device.vrmConfig.VrmOutputConfig
import org.vreo.devicehub.features.core.domain.device.vrmConfig.VrmOutputType
import org.vreo.devicehub.features.vrmOutput.domain.models.VrmOverlayerPosition

import vreo.volt.protocol.VrmConfiguration
import vreo.volt.protocol.VrmConfigurationList

/**
 * Map list of VRM output configuration to VRM configuration protobuf message
 *
 * @return list of Vrm configuration protobuf message
 */
fun List<VrmOutputConfig>.toVrmConfigurationListProto(): VrmConfigurationList {
    return VrmConfigurationList(vrms = this.map { it.toVrmConfigurationProto() })
}

/**
 * Map list of VRM configurations protobuf message to list of VRM output configuration
 *
 * @return list of VRM output configuration
 */
fun VrmConfigurationList.toVrmOutputConfigurations(): List<VrmOutputConfig> {
    return this.vrms.map { it.toVrmOutputConfig() }
}

/**
 * Converts a [VrmOutputConfig] to a [VrmConfiguration] proto message.
 */
private fun VrmOutputConfig.toVrmConfigurationProto(): VrmConfiguration {
    return VrmConfiguration(
        id = this.id,
        enabled = this.enabled,
        vrm_type = this.vrmType.value,
        endpoint = this.endpoint,
        api_key = this.apiKey,
        site_id = this.siteId,
        use_roi_id = this.useRoiId,
        bay_identifier = this.bayIdentifier,
        confidence_threshold = this.confidenceThreshold,
        timeout_seconds = this.timeoutSeconds,
        verify_ssl = this.verifySsl,
        enable_overlayer = this.enableOverlayer,
        overlayer_position_index = this.overlayerPosition.ordinal,
        overlayer_label = this.overlayerLabel ?: "",
        enable_overlayer_ocr_results = this.enableOverlayerOcrResults,
        enable_overlayer_bay_id = this.enableOverlayerBayId,
        enable_overlayer_site_id = this.enableOverlayerSiteId,
        time_zone = this.timeZone ?: ""
    )
}

/**
 * Converts a [VrmConfiguration] proto message to a [VrmOutputConfig].
 */
private fun VrmConfiguration.toVrmOutputConfig(): VrmOutputConfig {
    // Get VRM type
    val vrmType = when (this.vrm_type) {
        VrmOutputType.ZATPARK.value -> VrmOutputType.ZATPARK
        VrmOutputType.APT.value -> VrmOutputType.APT
        VrmOutputType.SUREPARK.value -> VrmOutputType.SUREPARK
        else -> VrmOutputType.OTHER
    }

    // To support older version of protobuf (without overlayer) set default to false
    val overlayerEnabled = try {
        this.enable_overlayer
    } catch (_: Exception) {
        false
    }

    return if (overlayerEnabled) {
        // Get overlayer position, first as default
        val overLayerPosition = try {
            VrmOverlayerPosition.entries[this.overlayer_position_index]
        } catch (_: Exception) {
            VrmOverlayerPosition.TOP_LEFT
        }

        VrmOutputConfig(
            id = this.id,
            enabled = this.enabled,
            vrmType = vrmType,
            endpoint = this.endpoint,
            apiKey = this.api_key,
            siteId = this.site_id,
            useRoiId = this.use_roi_id,
            bayIdentifier = this.bay_identifier,
            confidenceThreshold = this.confidence_threshold,
            timeoutSeconds = this.timeout_seconds,
            verifySsl = this.verify_ssl,
            enableOverlayer = overlayerEnabled,
            overlayerPosition = overLayerPosition,
            overlayerLabel = this.overlayer_label,
            enableOverlayerOcrResults = this.enable_overlayer_ocr_results,
            enableOverlayerBayId = this.enable_overlayer_bay_id,
            enableOverlayerSiteId = this.enable_overlayer_site_id,
            timeZone = this.time_zone
        )
    } else {
        VrmOutputConfig(
            id = this.id,
            enabled = this.enabled,
            vrmType = vrmType,
            endpoint = this.endpoint,
            apiKey = this.api_key,
            siteId = this.site_id,
            useRoiId = this.use_roi_id,
            bayIdentifier = this.bay_identifier,
            confidenceThreshold = this.confidence_threshold,
            timeoutSeconds = this.timeout_seconds,
            verifySsl = this.verify_ssl,
            enableOverlayer = overlayerEnabled
        )
    }
}